/**
 * Author : Komal Sadrani
 * Changes : Aaditya Gandhare
 * Modified : 13th Aug, 2018
 */

/**
 * Ext Libs
 */
import {Component, OnInit} from '@angular/core';
import {Router, ActivatedRoute, Params} from '@angular/router';
import {FormBuilder, FormGroup, FormControl, Validators} from '@angular/forms';
import {LocalStorageService} from 'ngx-store';

/**
 * Services
 */
import {UserService} from '../../providers/user.service';
import {BaseSettingsService} from '../../providers/base-settings.service';

@Component({
  selector: 'login-page',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  public loginForm: FormGroup;
  public loggingin = false;
  public responseMessage: string;
  public errorMessage: string;
  public loginButtonText: string;

  private referrer: string;
  private file_name: string;

  /**
   * Initialize the class
   */
  constructor(private router: Router, private formBuilder: FormBuilder, private userService: UserService,
    private localStorage: LocalStorageService, private baseSettings: BaseSettingsService, private activatedRoute: ActivatedRoute) {
    // Login Button
    this.loginButtonText = 'Login';

    // Setting up Login Form Group
    this.loginForm = this.formBuilder.group({
      username: ['', [Validators.required, Validators.email, Validators.minLength(6)]],
      password: ['', [Validators.required, Validators.minLength(6)]],
    });

  }

  /**
   * On Init Lifecycle Hook
   */
  ngOnInit() {
    // Get the Id of the Customer from the URL
    this.activatedRoute.params.subscribe((params: Params) => {
      this.referrer = params['referrer'];
      this.file_name = params['file_name'];
      this.checkSession();
    });
  }

  /**
   * Check if user already logged in
   */
  checkSession() {
    const customer = this.localStorage.get(this.baseSettings.customerSessionKey);
    // Check If Customer ID exists
    if (customer && customer.id) {
      // If so, redirect to Consignments Listing
      if (this.referrer && this.file_name) {
        this.router.navigate(['/consignments-list', this.referrer, this.file_name]);
      } else {
        this.router.navigate(['/consignments-list']);
      }
    }
  }

  /**
   * Submit Login Form
   */
  login() {
    // Prerequisites
    this.loggingin = true;
    this.errorMessage = '';
    this.loginButtonText = 'Logging In...';

    // Set Params
    const params = {
      'username_c': this.loginForm.controls.username.value,
      'password_c': this.loginForm.controls.password.value
    };

    // Ask Service to request Login
    this.userService.login(params).subscribe(
      (data) => {
        this.handleLoginResponseSuccess(data);
      },
      (err) => {
        this.handleLoginResponseError(err);
      },
    );
  }

  /**
   * Handle Login Success Response
   */
  handleLoginResponseSuccess(response) {
    this.loggingin = false;
    this.loginButtonText = 'Login';

    if (response.status == 'SUCCESS') {
      const session = {
        'id': response.customer.id,
        'name': response.customer.name,
        'email_c': response.customer.email_c,
        'address_c': response.customer.address_c,
        'contact_number_c': response.customer.contact_number_c,
        'postal_code_c': response.customer.postal_code_c,
        'username_c': response.customer.username_c,
      };
      this.localStorage.set(this.baseSettings.customerSessionKey, session);
      if (this.referrer && this.file_name) {
        this.router.navigate(['/consignments-list', this.referrer, this.file_name]);
      } else {
        this.router.navigate(['/consignments-list']);
      }
    }
  }

  /**
   * Handle Login Error Response
   */
  handleLoginResponseError(err) {
    let message = '';
    this.loggingin = false;
    this.loginButtonText = 'Login';

    // Bad Data or No data to the server
    if (err.status === 400) {
      message = 'Bad data passed!';
    }

    // Access Denies
    if (err.status === 403) {
      message = 'Access Denied!';
    }

    // Username or Password does not match
    if (err.status === 409) {
      message = 'Username or Password does not match';
    }

    // Error from Server
    if (err.status === 500) {
      message = 'Oops, something went wrong. Please try again.';
    }

    // No Network Error
    if (err.status === -1) {
      message = 'No Internet Connection. Please try to connect to a network and try again.';
    }

    this.errorMessage = message;
  }
}
